/**
 * Execution Progress Bar Component
 *
 * A compact bottom progress bar that shows execution status.
 * Can be expanded to show detailed execution steps.
 */

import React from 'react';
import {
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronUp,
  ChevronDown,
  History,
  Square,
} from 'lucide-react';
import {
  useExecutionState,
  useIsExecuting,
  useWorkflowStore,
} from '../hooks/useWorkflowStore';

interface ExecutionProgressBarProps {
  totalNodes: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenHistory: () => void;
  onStop?: () => void;
}

export const ExecutionProgressBar: React.FC<ExecutionProgressBarProps> = ({
  totalNodes,
  isExpanded,
  onToggleExpand,
  onOpenHistory,
  onStop,
}) => {
  const execution = useExecutionState();
  const isExecuting = useIsExecuting();
  const clearExecution = useWorkflowStore((s) => s.clearExecution);

  // Calculate progress
  const completedNodes = Object.values(execution.nodeStatuses).filter(
    (s) => s === 'completed'
  ).length;
  const failedNodes = Object.values(execution.nodeStatuses).filter(
    (s) => s === 'failed'
  ).length;
  const progress = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;

  // Get current running node name
  const nodes = useWorkflowStore((s) => s.nodes);
  const runningNodeKey = Object.entries(execution.nodeStatuses).find(
    ([, status]) => status === 'running'
  )?.[0];
  const runningNode = nodes.find(
    (n) => n.data.nodeKey === runningNodeKey || n.id.replace(/^rf-/, '') === runningNodeKey
  );

  // Calculate duration
  const getDuration = () => {
    if (!execution.startedAt) return null;
    const start = new Date(execution.startedAt).getTime();
    const end = execution.completedAt
      ? new Date(execution.completedAt).getTime()
      : Date.now();
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Status display
  const getStatusDisplay = () => {
    if (isExecuting) {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: runningNode ? `Running: ${runningNode.data.label}` : 'Running...',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
      };
    }
    if (execution.status === 'completed') {
      return {
        icon: <CheckCircle2 className="w-4 h-4" />,
        text: 'Completed',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
      };
    }
    if (execution.status === 'failed') {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: 'Failed',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
      };
    }
    if (execution.status === 'partial') {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: 'Partial',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
      };
    }
    return {
      icon: <Play className="w-4 h-4" />,
      text: 'Ready',
      color: 'text-gray-500',
      bgColor: 'bg-gray-50',
    };
  };

  const status = getStatusDisplay();
  const hasExecution = execution.executionId || execution.status;

  // If no execution yet, show minimal bar
  if (!hasExecution) {
    return (
      <div className="border-t bg-gray-50 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Play className="w-4 h-4" />
          <span>No execution yet</span>
        </div>
        <button
          onClick={onOpenHistory}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <History className="w-4 h-4" />
          <span>History</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`border-t ${status.bgColor} transition-colors`}>
      {/* Main progress bar */}
      <div className="px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Status */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`flex items-center gap-2 ${status.color}`}>
              {status.icon}
              <span className="font-medium text-sm whitespace-nowrap">{status.text}</span>
            </div>

            {/* Progress info */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>
                {completedNodes}/{totalNodes} nodes
              </span>
              {failedNodes > 0 && (
                <span className="text-red-500">({failedNodes} failed)</span>
              )}
              {getDuration() && (
                <span className="text-gray-400">| {getDuration()}</span>
              )}
            </div>
          </div>

          {/* Center: Progress bar */}
          <div className="flex-1 max-w-xs">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  failedNodes > 0
                    ? 'bg-red-500'
                    : isExecuting
                    ? 'bg-blue-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {isExecuting && onStop && (
              <button
                onClick={onStop}
                className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-100 rounded transition-colors"
              >
                <Square className="w-3 h-3" />
                Stop
              </button>
            )}

            {!isExecuting && hasExecution && (
              <button
                onClick={clearExecution}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                Clear
              </button>
            )}

            <button
              onClick={onOpenHistory}
              className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              <History className="w-4 h-4" />
            </button>

            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Details</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error summary (if any) */}
      {execution.error && !isExpanded && (
        <div className="px-4 pb-2">
          <div className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded truncate">
            {execution.error}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionProgressBar;
